import { getRawInitData } from "./telegram";

const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "";

// Session-token layer: raw initData is only ever sent to the server
// ONCE, here, to be exchanged for a session token - every other
// request in this file uses that token instead. This exists because
// initData travels in the Mini App's own URL (its hash fragment),
// which means it can simply be copied out of the browser and reused
// by anyone who gets hold of the link; the backend now only accepts a
// given initData ONE time (see /api/auth/start), so re-sending the
// raw value on every request the way this used to work would mean
// every request after the very first one gets rejected. The session
// token itself never appears in a URL, so it doesn't carry the same
// "copy the link, get in" risk.
let sessionTokenPromise: Promise<string> | null = null;

async function exchangeForSessionToken(): Promise<string> {
  const initData = getRawInitData();
  const res = await fetch(`${API_BASE_URL}/api/auth/start`, {
    method: "POST",
    headers: { Authorization: `tma ${initData}` },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      /* not JSON, keep statusText */
    }
    throw new ApiError(res.status, detail);
  }
  const body = (await res.json()) as { session_token: string };
  return body.session_token;
}

/** Lazily exchanges initData for a session token on the first call in
 * this page load, then reuses the SAME in-flight/resolved promise for
 * every call after that - guarantees the exchange (and therefore the
 * one-time initData consumption) happens exactly once, even if
 * several requests fire before the first exchange has finished. */
async function getSessionToken(): Promise<string> {
  if (!sessionTokenPromise) {
    sessionTokenPromise = exchangeForSessionToken().catch((err) => {
      // Let the NEXT call retry the exchange instead of permanently
      // caching a failure (e.g. a transient network error shouldn't
      // brick the whole session for the rest of the page load).
      sessionTokenPromise = null;
      throw err;
    });
  }
  return sessionTokenPromise;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function stringifyErrorDetail(detail: unknown, fallback: string): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // FastAPI/Pydantic 422 validation errors: a list of
    // {loc, msg, type} objects, not a plain string.
    const messages = detail
      .map((item) => (item && typeof item === "object" && "msg" in item ? String((item as { msg: unknown }).msg) : null))
      .filter((m): m is string => !!m);
    if (messages.length) return messages.join(" / ");
  }
  if (detail && typeof detail === "object") {
    try {
      return JSON.stringify(detail);
    } catch {
      /* fall through to fallback below */
    }
  }
  return fallback;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const sessionToken = await getSessionToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `tma ${sessionToken}`,
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    let detail: string = res.statusText;
    try {
      const body = await res.json();
      detail = stringifyErrorDetail(body?.detail, res.statusText);
    } catch {
      /* response wasn't JSON, keep statusText */
    }
    throw new ApiError(res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function requestArrayBuffer(path: string): Promise<ArrayBuffer> {
  const sessionToken = await getSessionToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `tma ${sessionToken}` },
  });
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText);
  }
  return res.arrayBuffer();
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  getArrayBuffer: requestArrayBuffer,
};
